/**
 * Affiliate Contract Interface - Handles interaction with the Solana program
 */

import { Connection, PublicKey, Transaction, SystemProgram } from '@solana/web3.js';
import { AnchorProvider, Program, BN } from '@project-serum/anchor';
import { TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { AFFILIATE_COMMISSION_RATE } from './constants';

// Constants
const SYSVAR_RENT_PUBKEY = new PublicKey('SysvarRent111111111111111111111111111111111');

// This would be replaced with the actual program ID
const AFFILIATE_PROGRAM_ID = new PublicKey('Affi1iateXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX');

/**
 * Initialize the affiliate program (admin only)
 * @param {AnchorProvider} provider - Anchor provider with wallet
 * @param {PublicKey} tokenMint - JINU token mint
 * @param {PublicKey} tokenVault - Token vault for affiliate rewards
 * @returns {Promise<string>} - Transaction signature
 */
export const initializeAffiliateProgram = async (provider, tokenMint, tokenVault) => {
  try {
    const program = getAffiliateProgram(provider);
    
    // Derive the affiliate pool PDA
    const [affiliatePool, bump] = await PublicKey.findProgramAddress(
      [Buffer.from('affiliate_pool')],
      program.programId
    );
    
    // Initialize the program
    const tx = await program.methods
      .initialize(bump)
      .accounts({
        authority: provider.wallet.publicKey,
        tokenMint,
        affiliatePool,
        tokenVault,
        systemProgram: SystemProgram.programId,
        tokenProgram: TOKEN_PROGRAM_ID,
        rent: SYSVAR_RENT_PUBKEY,
      })
      .rpc();
    
    return tx;
  } catch (error) {
    console.error('Error initializing affiliate program:', error);
    throw error;
  }
};

/**
 * Register a user as a referrer
 * @param {AnchorProvider} provider - Anchor provider with wallet
 * @returns {Promise<string>} - Transaction signature
 */
export const registerAsReferrer = async (provider) => {
  try {
    const program = getAffiliateProgram(provider);
    const walletPubkey = provider.wallet.publicKey;
    
    // Derive the referrer PDA
    const [referrer, bump] = await PublicKey.findProgramAddress(
      [Buffer.from('referrer'), walletPubkey.toBuffer()],
      program.programId
    );
    
    // Register as referrer
    const tx = await program.methods
      .registerReferrer()
      .accounts({
        owner: walletPubkey,
        referrer,
        systemProgram: SystemProgram.programId,
        rent: SYSVAR_RENT_PUBKEY,
      })
      .rpc();
    
    return tx;
  } catch (error) {
    console.error('Error registering as referrer:', error);
    throw error;
  }
};

/**
 * Record a referral when a user contributes
 * @param {AnchorProvider} provider - Anchor provider with wallet
 * @param {PublicKey} referrerWallet - Wallet of the referrer
 * @param {number} solAmount - Amount of SOL contributed
 * @param {number} jinuAmount - Amount of JINU tokens received
 * @returns {Promise<string>} - Transaction signature
 */
export const recordReferral = async (provider, referrerWallet, solAmount, jinuAmount) => {
  try {
    const program = getAffiliateProgram(provider);
    const walletPubkey = provider.wallet.publicKey;
    
    // Derive the referrer PDA
    const [referrer, _] = await PublicKey.findProgramAddress(
      [Buffer.from('referrer'), referrerWallet.toBuffer()],
      program.programId
    );
    
    // Derive the referred PDA
    const [referred, __] = await PublicKey.findProgramAddress(
      [Buffer.from('referred'), walletPubkey.toBuffer()],
      program.programId
    );
    
    // Derive the affiliate pool PDA
    const [affiliatePool, ___] = await PublicKey.findProgramAddress(
      [Buffer.from('affiliate_pool')],
      program.programId
    );
    
    // Convert amounts to lamports/smallest unit
    const solAmountLamports = new BN(solAmount * 1e9); // Convert SOL to lamports
    const jinuAmountSmallest = new BN(jinuAmount * 1e9); // Assuming 9 decimals for JINU
    
    // Record the referral
    const tx = await program.methods
      .recordReferral(solAmountLamports, jinuAmountSmallest)
      .accounts({
        referrer,
        referredOwner: walletPubkey,
        referred,
        affiliatePool,
        systemProgram: SystemProgram.programId,
        rent: SYSVAR_RENT_PUBKEY,
      })
      .rpc();
    
    return tx;
  } catch (error) {
    console.error('Error recording referral:', error);
    throw error;
  }
};

/**
 * Claim affiliate rewards
 * @param {AnchorProvider} provider - Anchor provider with wallet
 * @param {number} amount - Amount of JINU to claim
 * @param {PublicKey} userTokenAccount - User's JINU token account
 * @returns {Promise<string>} - Transaction signature
 */
export const claimAffiliateRewards = async (provider, amount, userTokenAccount) => {
  try {
    const program = getAffiliateProgram(provider);
    const walletPubkey = provider.wallet.publicKey;
    
    // Derive the referrer PDA
    const [referrer, _] = await PublicKey.findProgramAddress(
      [Buffer.from('referrer'), walletPubkey.toBuffer()],
      program.programId
    );
    
    // Derive the affiliate pool PDA
    const [affiliatePool, __] = await PublicKey.findProgramAddress(
      [Buffer.from('affiliate_pool')],
      program.programId
    );
    
    // Get the token vault from the affiliate pool
    const affiliatePoolAccount = await program.account.affiliatePool.fetch(affiliatePool);
    const tokenVault = affiliatePoolAccount.tokenVault;
    
    // Convert amount to smallest unit
    const amountSmallest = new BN(amount * 1e9); // Assuming 9 decimals for JINU
    
    // Claim rewards
    const tx = await program.methods
      .claimRewards(amountSmallest)
      .accounts({
        owner: walletPubkey,
        referrer,
        affiliatePool,
        tokenVault,
        userTokenAccount,
        tokenProgram: TOKEN_PROGRAM_ID,
        systemProgram: SystemProgram.programId,
      })
      .rpc();
    
    return tx;
  } catch (error) {
    console.error('Error claiming affiliate rewards:', error);
    throw error;
  }
};

/**
 * Get referrer stats
 * @param {Connection} connection - Solana connection
 * @param {PublicKey} walletPubkey - Wallet public key
 * @returns {Promise<Object>} - Referrer stats
 */
export const getReferrerStats = async (connection, walletPubkey) => {
  try {
    const provider = getProviderFromConnection(connection, walletPubkey);
    const program = getAffiliateProgram(provider);
    
    // Derive the referrer PDA
    const [referrer, _] = await PublicKey.findProgramAddress(
      [Buffer.from('referrer'), walletPubkey.toBuffer()],
      program.programId
    );
    
    try {
      // Fetch referrer account
      const referrerAccount = await program.account.referrer.fetch(referrer);
      
      return {
        count: referrerAccount.totalReferrals.toNumber(),
        volume: referrerAccount.totalVolume.toNumber() / 1e9, // Convert from lamports to SOL
        earnings: referrerAccount.totalRewards.toNumber() / 1e9, // Convert to JINU
        claimed: referrerAccount.claimedRewards.toNumber() / 1e9,
        unclaimed: (referrerAccount.totalRewards.toNumber() - referrerAccount.claimedRewards.toNumber()) / 1e9
      };
    } catch (error) {
      // If account doesn't exist yet, return default values
      return {
        count: 0,
        volume: 0,
        earnings: 0,
        claimed: 0,
        unclaimed: 0
      };
    }
  } catch (error) {
    console.error('Error getting referrer stats:', error);
    throw error;
  }
};

/**
 * Check if a user was referred
 * @param {Connection} connection - Solana connection
 * @param {PublicKey} walletPubkey - Wallet public key
 * @returns {Promise<PublicKey|null>} - Referrer wallet or null
 */
export const checkIfReferred = async (connection, walletPubkey) => {
  try {
    const provider = getProviderFromConnection(connection, walletPubkey);
    const program = getAffiliateProgram(provider);
    
    // Derive the referred PDA
    const [referred, _] = await PublicKey.findProgramAddress(
      [Buffer.from('referred'), walletPubkey.toBuffer()],
      program.programId
    );
    
    try {
      // Fetch referred account
      const referredAccount = await program.account.referred.fetch(referred);
      
      // Return the referrer if it exists
      return referredAccount.referrer || null;
    } catch (error) {
      // If account doesn't exist, user wasn't referred
      return null;
    }
  } catch (error) {
    console.error('Error checking if user was referred:', error);
    return null;
  }
};

// Helper function to get the Anchor program
const getAffiliateProgram = (provider) => {
  // Note: In a real implementation, you would load the IDL from a file
  // For this example, we're assuming the IDL is available
  return new Program(AffiliateIDL, AFFILIATE_PROGRAM_ID, provider);
};

// Helper function to create a provider from a connection
const getProviderFromConnection = (connection, walletPubkey) => {
  // Create a read-only wallet
  const wallet = {
    publicKey: walletPubkey,
    signTransaction: async (tx) => tx,
    signAllTransactions: async (txs) => txs,
  };
  
  return new AnchorProvider(connection, wallet, { commitment: 'confirmed' });
};

// Placeholder for the IDL - in a real implementation, this would be imported
const AffiliateIDL = {
  // This would be the actual IDL generated from the Anchor program
  // For brevity, it's omitted here
};
